import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { Restaurant } from '@/types'
import ApiService from '@/services/api'
import { useAuth } from './AuthContext'
import { DEFAULT_COUNTRY, getCurrencyForCountry } from '@/lib/countries'

export type RestaurantViewMode = 'restaurant' | 'combined'

interface RestaurantContextType {
  restaurants: Restaurant[]
  currentRestaurant: Restaurant | null
  isLoading: boolean
  error: string | null
  selectRestaurant: (restaurantId: string) => Promise<void>
  refreshRestaurants: () => Promise<void>
  hasMultipleRestaurants: boolean
  viewMode: RestaurantViewMode
  selectCombinedView: () => void
}

const RestaurantContext = createContext<RestaurantContextType | null>(null)

const SELECTED_RESTAURANT_KEY = 'selectedRestaurantId'

export function RestaurantProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [currentRestaurant, setCurrentRestaurant] = useState<Restaurant | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<RestaurantViewMode>('restaurant')

  // Fetch all restaurants for the current user
  const fetchRestaurants = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setRestaurants([])
      setCurrentRestaurant(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = await ApiService.getUserRestaurants()

      if (response.success && response.data.restaurants) {
        const restaurantList = response.data.restaurants
        setRestaurants(restaurantList)

        // Determine which restaurant to select
        let restaurantToSelect: Restaurant | null = null

        // 1. Try to use previously selected restaurant from localStorage
        const savedRestaurantId = localStorage.getItem(SELECTED_RESTAURANT_KEY)
        if (savedRestaurantId) {
          restaurantToSelect = restaurantList.find((r: Restaurant) => r._id === savedRestaurantId) || null
        }

        // 2. Fallback to user's primary restaurantId (backward compatibility)
        if (!restaurantToSelect && user.restaurantId) {
          restaurantToSelect = restaurantList.find((r: Restaurant) => r._id === user.restaurantId) || null
        }

        // 3. Fallback to first restaurant in the list
        if (!restaurantToSelect && restaurantList.length > 0) {
          restaurantToSelect = restaurantList[0]
        }

        if (restaurantToSelect) {
          setCurrentRestaurant(restaurantToSelect)
          localStorage.setItem(SELECTED_RESTAURANT_KEY, restaurantToSelect._id)
        }
      } else {
        // Backward compatibility: if API doesn't return restaurants array,
        // try to use the old single restaurant approach
        if (user.restaurantId) {
          const singleRestaurant: Restaurant = {
            _id: user.restaurantId,
            name: user.name || 'My Restaurant',
            databaseName: user.databaseName || '',
            subscriptionStatus: user.subscriptionStatus || 'expired',
            subscriptionStartDate: user.subscriptionStartDate || new Date(),
            subscriptionEndDate: user.subscriptionEndDate || new Date(),
            paymentStatus: user.paymentStatus || 'pending',
            subscriptionAmount: user.subscriptionAmount || 12000,
            // Fill in defaults for required fields
            ownerName: user.name,
            email: user.email,
            phone: user.phone,
            country: DEFAULT_COUNTRY,
            currency: getCurrencyForCountry(DEFAULT_COUNTRY),
            address: {
              line1: '',
              line2: '',
              city: '',
              state: '',
              pincode: ''
            },
            gstin: '',
            planType: 'yearly',
            adminNotes: '',
            isActive: true,
            role: 'owner',
            createdAt: new Date(),
            updatedAt: new Date()
          }
          setRestaurants([singleRestaurant])
          setCurrentRestaurant(singleRestaurant)
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch restaurants:', err)
      setError(err.message || 'Failed to load restaurants')

      // Fallback to backward compatibility mode
      if (user?.restaurantId) {
        const singleRestaurant: Restaurant = {
          _id: user.restaurantId,
          name: user.name || 'My Restaurant',
          databaseName: user.databaseName || '',
          subscriptionStatus: user.subscriptionStatus || 'expired',
          subscriptionStartDate: user.subscriptionStartDate || new Date(),
          subscriptionEndDate: user.subscriptionEndDate || new Date(),
          paymentStatus: user.paymentStatus || 'pending',
          subscriptionAmount: user.subscriptionAmount || 12000,
          ownerName: user.name,
          email: user.email,
          phone: user.phone,
          country: DEFAULT_COUNTRY,
          currency: getCurrencyForCountry(DEFAULT_COUNTRY),
          address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            pincode: ''
          },
          gstin: '',
          planType: 'yearly',
          adminNotes: '',
          isActive: true,
          role: 'owner',
          createdAt: new Date(),
          updatedAt: new Date()
        }
        setRestaurants([singleRestaurant])
        setCurrentRestaurant(singleRestaurant)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, user])

  // Select a specific restaurant
  const selectRestaurant = useCallback(async (restaurantId: string) => {
    const restaurant = restaurants.find(r => r._id === restaurantId)

    if (restaurant) {
      setCurrentRestaurant(restaurant)
      // Picking an individual restaurant always exits the combined ("All
      // Restaurants") view, even if it was previously active.
      setViewMode('restaurant')
      localStorage.setItem(SELECTED_RESTAURANT_KEY, restaurantId)

      // Optionally fetch fresh details for the selected restaurant
      try {
        const response = await ApiService.getRestaurantDetails(restaurantId)
        if (response.success && response.data) {
          // Update the restaurant in the list with fresh data
          setRestaurants(prev => prev.map(r =>
            r._id === restaurantId ? { ...r, ...response.data } : r
          ))
          setCurrentRestaurant(prev => prev ? { ...prev, ...response.data } : null)
        }
      } catch (err) {
        console.error('Failed to fetch restaurant details:', err)
        // Continue with cached data
      }
    } else {
      console.warn(`Restaurant with ID ${restaurantId} not found`)
    }
  }, [restaurants])

  // Refresh restaurant list
  const refreshRestaurants = useCallback(async () => {
    await fetchRestaurants()
  }, [fetchRestaurants])

  // Fetch restaurants when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.userType === 'restaurant_owner') {
      fetchRestaurants()
    } else {
      setRestaurants([])
      setCurrentRestaurant(null)
      setIsLoading(false)
    }
  }, [isAuthenticated, user?.userType, fetchRestaurants])

  const hasMultipleRestaurants = restaurants.length > 1

  // Combined ("All Restaurants") view only makes sense for Head of Restaurant
  // owners — guard against stale state if an owner's restaurant count ever
  // drops to 1 while combined mode was active (e.g. admin unassigns a restaurant).
  const selectCombinedView = useCallback(() => {
    if (hasMultipleRestaurants) {
      setViewMode('combined')
    }
  }, [hasMultipleRestaurants])

  useEffect(() => {
    if (viewMode === 'combined' && !hasMultipleRestaurants) {
      setViewMode('restaurant')
    }
  }, [viewMode, hasMultipleRestaurants])

  const value: RestaurantContextType = {
    restaurants,
    currentRestaurant,
    isLoading,
    error,
    selectRestaurant,
    refreshRestaurants,
    hasMultipleRestaurants,
    viewMode,
    selectCombinedView
  }

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  )
}

export function useRestaurant() {
  const context = useContext(RestaurantContext)
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider')
  }
  return context
}

export { RestaurantContext }
